/**
 * 图层像素的整数平移（M3-11 裁剪用）。
 *
 * new(x, y) = old(x + offsetX, y + offsetY)，纯行拷贝、逐字节精确——
 * 裁剪不该有任何重采样（Lanczos 在整数位移上理论恒等，浮点上未必逐字节）。
 *
 * 事务纪律与画笔相同：先 touch 全部受影响块（旧 ∪ 新网格）、capture，
 * 再读旧写新。旧块中未被新内容覆盖的部分清零——裁掉的像素就是删除。
 */
import { TILE, tileKey, type LayerId } from './key';
import { BYTES_PER_TILE, type TileStore } from './store';

export interface ShiftTransaction {
  touch(key: ReturnType<typeof tileKey>): void;
  capture(): void;
}

/**
 * 平移一个图层的全部 lod-0 像素。
 *
 * @param offsetX 文档空间：新坐标系原点在旧坐标系里的 x（裁剪矩形的 x）。
 * @param newWidth 新画布宽（像素）。范围之外的内容被丢弃。
 * @returns 实际被写入的新块坐标（渲染失效用）。
 */
export function shiftLayerPixels(
  store: TileStore,
  layer: LayerId,
  offsetX: number,
  offsetY: number,
  newWidth: number,
  newHeight: number,
  transaction: ShiftTransaction,
): Array<{ tx: number; ty: number }> {
  // 抓住旧数据的引用：store.set 换的是存储槽，不动我们手里的数组
  const old = new Map<string, Uint8Array>();
  for (const tile of store.residentTilesOf(layer, 0)) {
    if (tile.data !== null) old.set(`${tile.key.tx} ${tile.key.ty}`, tile.data);
  }
  if (old.size === 0) return [];

  const newTx1 = Math.floor((newWidth - 1) / TILE);
  const newTy1 = Math.floor((newHeight - 1) / TILE);

  // touch：所有旧块 + 新网格里可能被写的块
  for (const id of old.keys()) {
    const [tx, ty] = id.split(' ').map(Number) as [number, number];
    transaction.touch(tileKey(layer, tx, ty));
  }
  for (let ty = 0; ty <= newTy1; ty++) {
    for (let tx = 0; tx <= newTx1; tx++) {
      transaction.touch(tileKey(layer, tx, ty));
    }
  }
  transaction.capture();

  const written = new Set<string>();
  const touched: Array<{ tx: number; ty: number }> = [];

  for (let nty = 0; nty <= newTy1; nty++) {
    for (let ntx = 0; ntx <= newTx1; ntx++) {
      const out = new Uint8Array(BYTES_PER_TILE);
      let hasInk = false;
      const rowTop = nty * TILE;
      const rowBottom = Math.min((nty + 1) * TILE, newHeight);
      const colLeft = ntx * TILE;
      const colRight = Math.min((ntx + 1) * TILE, newWidth);

      for (let y = rowTop; y < rowBottom; y++) {
        const sy = y + offsetY;
        if (sy < 0) continue;
        const sty = Math.floor(sy / TILE);
        const rowInSrc = sy % TILE;
        // 一行可能跨两个源块
        let x = colLeft;
        while (x < colRight) {
          const sx = x + offsetX;
          if (sx < 0) {
            x = -offsetX; // 跳到源 x=0 对应的位置
            continue;
          }
          const stx = Math.floor(sx / TILE);
          const src = old.get(`${stx} ${sty}`);
          const spanEnd = Math.min(colRight, (stx + 1) * TILE - offsetX);
          if (src !== undefined) {
            const srcStart = (rowInSrc * TILE + (sx % TILE)) * 4;
            const length = (spanEnd - x) * 4;
            const dstStart = ((y % TILE) * TILE + (x % TILE)) * 4;
            out.set(src.subarray(srcStart, srcStart + length), dstStart);
            hasInk = true;
          }
          x = spanEnd;
        }
      }

      const id = `${ntx} ${nty}`;
      const existed = old.has(id);
      if (hasInk) {
        store.set(tileKey(layer, ntx, nty), out);
        written.add(id);
        touched.push({ tx: ntx, ty: nty });
      } else if (existed) {
        // 新内容为空但旧块有像素：清零，否则残留旧画面
        store.set(tileKey(layer, ntx, nty), out);
        written.add(id);
        touched.push({ tx: ntx, ty: nty });
      }
    }
  }

  // 新网格之外的旧块全部清零（裁掉即删除）
  for (const id of old.keys()) {
    if (written.has(id)) continue;
    const [tx, ty] = id.split(' ').map(Number) as [number, number];
    if (tx <= newTx1 && ty <= newTy1) continue; // 网格内的空块已在上面处理
    store.set(tileKey(layer, tx, ty), new Uint8Array(BYTES_PER_TILE));
    touched.push({ tx, ty });
  }

  return touched;
}

/**
 * 移动图层（DESIGN.md §6.5 补充，移动工具；ADR-0012）——跟裁剪同样是"整数
 * 平移+行拷贝"，但语义完全不同，不能复用 `shiftLayerPixels`：裁剪要按
 * `newWidth`/`newHeight` 丢弃画布外内容，这是它自己想要的行为；移动工具
 * 要做到真正无损——挪到画布外的那部分只是暂时不可见，来回拖多少次都要
 * 原样恢复，包括往左/往上拖过画布原点（对应块坐标变负）这个方向。
 *
 * 之前这里直接拿文档画布宽高、后来又改成"旧内容占据的块，上界不超过
 * 画布"当 `shiftLayerPixels` 的目标网格上界——两次都还是把下界锁死在 0，
 * 往左/往上拖出原点的那部分因为 `tileKey` 曾经硬性要求块坐标非负
 * （ADR-0012 之前），物理上没有格子能存，永久丢失。ADR-0012 放开这条
 * 约束之后，这里改成完全有符号的目标网格：从旧内容实际占据的块范围
 * （不是画布尺寸）整体平移 offset 像素、按块边界重新量出上下界，不夹到 0。
 *
 * 行拷贝算法本身跟 `shiftLayerPixels` 同构（迭代目标块、每行按源坐标定位
 * 源块、跨块时分段拷贝），但两处"块内局部偏移"不能再用截断取模
 * （`x % TILE`）：JS 的 `%` 对负数是截断取模，`-10 % 256 === -10`，不是
 * `246`——一旦源/目标坐标可以为负，取模必须换成"减去已经算出来的块原点"
 * （`x - tx*TILE`，对任意符号都对）。
 */
export function shiftLayerPixelsForMove(
  store: TileStore,
  layer: LayerId,
  offsetX: number,
  offsetY: number,
  transaction: ShiftTransaction,
): Array<{ tx: number; ty: number }> {
  const old = new Map<string, Uint8Array>();
  let minTx = Infinity;
  let minTy = Infinity;
  let maxTx = -Infinity;
  let maxTy = -Infinity;
  for (const tile of store.residentTilesOf(layer, 0)) {
    if (tile.data === null) continue;
    old.set(`${tile.key.tx} ${tile.key.ty}`, tile.data);
    if (tile.key.tx < minTx) minTx = tile.key.tx;
    if (tile.key.tx > maxTx) maxTx = tile.key.tx;
    if (tile.key.ty < minTy) minTy = tile.key.ty;
    if (tile.key.ty > maxTy) maxTy = tile.key.ty;
  }
  if (old.size === 0) return []; // 没有像素，没什么可移动

  // 目标网格：旧内容的块范围（换算成像素）整体平移后，按块边界重新量出的
  // 范围——完全有符号，这就是"不裁剪"的全部含义。new(x,y)=old(x+offsetX,
  // y+offsetY)，反过来 new_x = old_x - offsetX，所以这里要**减** offsetX
  // （不是加）——跟原来只支持右/下方向那版的 `width = (maxTx+1)*TILE -
  // offsetX` 是同一个符号，这里只是把下界也一并算出来。
  const newTx0 = Math.floor((minTx * TILE - offsetX) / TILE);
  const newTx1 = Math.floor(((maxTx + 1) * TILE - 1 - offsetX) / TILE);
  const newTy0 = Math.floor((minTy * TILE - offsetY) / TILE);
  const newTy1 = Math.floor(((maxTy + 1) * TILE - 1 - offsetY) / TILE);

  for (const id of old.keys()) {
    const [tx, ty] = id.split(' ').map(Number) as [number, number];
    transaction.touch(tileKey(layer, tx, ty));
  }
  for (let ty = newTy0; ty <= newTy1; ty++) {
    for (let tx = newTx0; tx <= newTx1; tx++) {
      transaction.touch(tileKey(layer, tx, ty));
    }
  }
  transaction.capture();

  const written = new Set<string>();
  const touched: Array<{ tx: number; ty: number }> = [];

  for (let nty = newTy0; nty <= newTy1; nty++) {
    for (let ntx = newTx0; ntx <= newTx1; ntx++) {
      const out = new Uint8Array(BYTES_PER_TILE);
      let hasInk = false;
      const rowTop = nty * TILE;
      const colLeft = ntx * TILE;

      for (let y = rowTop; y < rowTop + TILE; y++) {
        const sy = y + offsetY;
        const sty = Math.floor(sy / TILE);
        const rowInSrc = sy - sty * TILE; // 替代 sy % TILE，对负数也对
        const dstRow = y - rowTop;

        let x = colLeft;
        while (x < colLeft + TILE) {
          const sx = x + offsetX;
          const stx = Math.floor(sx / TILE);
          const src = old.get(`${stx} ${sty}`);
          const spanEnd = Math.min(colLeft + TILE, (stx + 1) * TILE - offsetX);
          if (src !== undefined) {
            const colInSrc = sx - stx * TILE; // 替代 sx % TILE
            const srcStart = (rowInSrc * TILE + colInSrc) * 4;
            const length = (spanEnd - x) * 4;
            const dstStart = (dstRow * TILE + (x - colLeft)) * 4; // 替代 x % TILE
            out.set(src.subarray(srcStart, srcStart + length), dstStart);
            hasInk = true;
          }
          x = spanEnd;
        }
      }

      const id = `${ntx} ${nty}`;
      if (hasInk || old.has(id)) {
        store.set(tileKey(layer, ntx, nty), out);
        written.add(id);
        touched.push({ tx: ntx, ty: nty });
      }
    }
  }

  // 孤块清零：旧网格里没有被上面处理过的块，说明它已经不在新网格范围内——
  // 不用重新比较范围，新网格里所有跟旧块重合的 id 在上面的双重循环里必然
  // 已经被 `old.has(id)` 分支写过一次并加入 written，所以这里判断
  // `written.has(id)` 就足够，不需要像旧版本那样再夹一次上下界。
  for (const id of old.keys()) {
    if (written.has(id)) continue;
    const [tx, ty] = id.split(' ').map(Number) as [number, number];
    store.set(tileKey(layer, tx, ty), new Uint8Array(BYTES_PER_TILE));
    touched.push({ tx, ty });
  }

  return touched;
}
